import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

const ALL_CHARACTERIZATIONS = [
  'Incremental innovation', 'Radical innovation', 'Disruptive innovation', 'Other',
];

const CHAR_COLORS = {
  'Incremental innovation': '#8b5cf6',
  'Radical innovation': '#f59e0b',
  'Disruptive innovation': '#ef4444',
  'Other': '#6b7280',
};

const ALL_TYPOLOGIES = [
  'Technological innovation',
  'Capacity development innovation',
  'Policy, organizational or institutional innovation',
  "Other/I'm not sure/This typology does not work for my innovation",
];

const TYPO_COLORS = ['#6366f1', '#22c55e', '#f97316', '#06b6d4'];

function InfoTooltip({ text }) {
  return (
    <div className="relative group inline-block ml-1 cursor-help align-middle">
      <span className="text-gray-400 text-xs">&#9432;</span>
      <div className="hidden group-hover:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 w-60 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none text-left leading-relaxed">
        {text}
      </div>
    </div>
  );
}

export const InnovationNatureChart = memo(function InnovationNatureChart() {
  const { natureTypologyData, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(130);
  if (!natureTypologyData) return (
    <ChartWrapper title="Innovation Nature & Typology">
      <NoChartData message="No Innovation Development results available" detail='This chart analyzes the characterization and typology fields reported on Innovation Development results (e.g. "Genetic - variety/breed", "Technology - digital"). The current selection has no Innovation Development results to analyze.' />
    </ChartWrapper>
  );

  const { total, characterizationCounts, typologyCounts, varietyBreedCount, plannedCount, unplannedCount } = natureTypologyData;

  const charData = ALL_CHARACTERIZATIONS.map(name => ({
    name,
    count: characterizationCounts[name] || 0,
    percentage: total > 0 ? (((characterizationCounts[name] || 0) / total) * 100).toFixed(1) : '0.0',
  }));

  const typoData = ALL_TYPOLOGIES.map(name => ({
    name,
    count: typologyCounts[name] || 0,
  }));

  const exportData = [
    ...charData.map(d => ({ Category: 'Characterization', Name: d.name, Count: d.count, Percentage: d.percentage + '%' })),
    ...typoData.map(d => ({ Category: 'Typology', Name: d.name, Count: d.count })),
    { Category: 'Summary', Name: 'Variety/Breed innovations', Count: varietyBreedCount },
    { Category: 'Summary', Name: 'Is Planned? Yes', Count: plannedCount },
    { Category: 'Summary', Name: 'Is Planned? No', Count: unplannedCount },
  ];

  return (
    <ChartWrapper title="Innovation Nature & Typology" info="Characterization of innovations as Incremental, Radical, or Disruptive, alongside their technology/practice typology." chartData={exportData} exportFilename="innovation-nature-typology" sourceResults={innovationResults}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1.5">
            Profiling a New Variety / Breed?
            <InfoTooltip text="Whether the innovation profiles a new or improved variety or breed. Source: Excel column 'Are you profiling a new or improved variety or breed?'" />
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{varietyBreedCount.toLocaleString()}</p>
              <p className="text-xs text-amber-600">Yes</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{(total - varietyBreedCount).toLocaleString()}</p>
              <p className="text-xs text-amber-600">No</p>
            </div>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1.5">
            Is Planned?
            <InfoTooltip text="Whether the innovation was planned in the original Theory of Change. Source: Excel column 'Is Planned?'" />
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{plannedCount.toLocaleString()}</p>
              <p className="text-xs text-blue-600">Yes</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{unplannedCount.toLocaleString()}</p>
              <p className="text-xs text-blue-600">No</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Characterization</h5>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={charData} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} tickFormatter={v => v.replace(' innovation', '')} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: CHAR_COLORS[d.name] || '#1e293b' }}>{d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''} ({d.percentage}%)</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} animationBegin={0} animationDuration={400}>
                {charData.map((entry) => (
                  <Cell key={entry.name} fill={CHAR_COLORS[entry.name] || '#6b7280'} />
                ))}
                <LabelList dataKey="count" position="top" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Typology</h5>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={typoData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 130 ? 9 : 11 }} width={yAxisWidth} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                const idx = typoData.indexOf(d);
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: TYPO_COLORS[idx >= 0 ? idx % TYPO_COLORS.length : 0] }}>{d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} animationBegin={0} animationDuration={400}>
                {typoData.map((entry, i) => (
                  <Cell key={entry.name} fill={TYPO_COLORS[i % TYPO_COLORS.length]} />
                ))}
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartWrapper>
  );
});
